import accounting from '../../mixins/accounting';

require('./scss/slider.scss');

export default {
    name: 'slider',

    template: `
        <div id="slider">
            <div class="row range-slider-values m-0">
                <input
                    ref="minInput"
                    class="slider-input pl-3"
                    type="text"
                    :value="formattedValue(currentMin)"
                    @change="setInputValue($event, 'min')"
                />
                <span class="dash"></span>
                <input
                    ref="maxInput"
                    class="slider-input pl-3"
                    type="text"
                    :value="formattedValue(currentMax)"
                    @change="setInputValue($event, 'max')"
                />
            </div>

            <div class="dual-range-slider" ref="track" @mousedown.left.prevent="onTrackClick" @touchstart.prevent="onTrackClick">
                <div class="dual-range-track-bg"></div>
                <div class="dual-range-track-fill" :style="fillStyle"></div>
                <div
                    class="dual-range-thumb"
                    :style="minThumbStyle"
                    @mousedown.left.stop.prevent="startDrag('min')"
                    @touchstart.stop.prevent="startDrag('min')"
                ></div>
                <div
                    class="dual-range-thumb"
                    :style="maxThumbStyle"
                    @mousedown.left.stop.prevent="startDrag('max')"
                    @touchstart.stop.prevent="startDrag('max')"
                ></div>
            </div>
        </div>
    `,

    mixins: [accounting],

    props: ['type', 'minValue', 'maxValue', 'setMinValue', 'setMaxValue', 'currency'],

    data() {
        return {
            currentMin: Math.floor(Number(this.minValue)) || 0,
            currentMax: Math.round(Number(this.maxValue)) || 0,
            absoluteMin: Math.floor(Number(this.minValue)) || 0,
            absoluteMax: Math.round(Number(this.maxValue)) || 0,
            dragging: null,
        };
    },

    computed: {
        fillStyle() {
            const min = this.absoluteMin;
            const max = this.absoluteMax;
            if (max === min) return {};
            const minPct = Math.max(0, Math.min(100, ((this.currentMin - min) / (max - min)) * 100));
            const maxPct = Math.max(0, Math.min(100, ((this.currentMax - min) / (max - min)) * 100));
            const left = Math.min(minPct, maxPct);
            const right = 100 - Math.max(minPct, maxPct);
            return { left: left + '%', right: right + '%' };
        },
        minThumbStyle() {
            const pct = this.thumbPercent(this.currentMin);
            return { left: pct + '%' };
        },
        maxThumbStyle() {
            const pct = this.thumbPercent(this.currentMax);
            return { left: pct + '%' };
        },
    },

    watch: {
        minValue(val) {
            const newAbsMin = Math.floor(Number(val) || 0);
            this.absoluteMin = newAbsMin;
            if (!this.dragging) {
                this.currentMin = Math.min(this.currentMax, Math.max(newAbsMin, this.currentMin));
            }
        },
        maxValue(val) {
            const newAbsMax = Math.round(Number(val) || 0);
            this.absoluteMax = newAbsMax;
            if (!this.dragging) {
                if (this.currentMax === 0 || this.currentMax > newAbsMax) {
                    this.currentMax = Math.max(this.currentMin, newAbsMax);
                }
            }
        },
    },

    methods: {
        thumbPercent(value) {
            const min = this.absoluteMin;
            const max = this.absoluteMax;
            if (max === min) return 0;
            const pct = ((value - min) / (max - min)) * 100;
            return Math.max(0, Math.min(100, pct));
        },
        formattedValue(value) {
            if (this.type === 'price') {
                return this.formatMoney(value, this.currency.code);
            }
            return value;
        },
        valueFromEvent(event) {
            const track = this.$refs.track;
            if (!track) return this.absoluteMin;
            const rect = track.getBoundingClientRect();
            if (!rect.width) return this.absoluteMin;
            const clientX = event.touches ? event.touches[0].clientX : event.clientX;
            const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
            const raw = this.absoluteMin + pct * (this.absoluteMax - this.absoluteMin);
            return Math.min(this.absoluteMax, Math.max(this.absoluteMin, Math.round(raw)));
        },
        startDrag(type) {
            if (this.dragging) this.stopDrag();
            this.dragging = type;
            document.addEventListener('mousemove', this.onDrag);
            document.addEventListener('mouseup', this.stopDrag);
            document.addEventListener('touchmove', this.onDrag, {
                passive: false,
            });
            document.addEventListener('touchend', this.stopDrag);
            document.addEventListener('touchcancel', this.stopDrag);
        },
        onDrag(event) {
            if (!this.dragging) return;
            if (event.cancelable) event.preventDefault();
            const val = this.valueFromEvent(event);
            if (this.dragging === 'min' && val <= this.currentMax && val !== this.currentMin) {
                this.currentMin = val;
                this.setMinValue(val);
            } else if (this.dragging === 'max' && val >= this.currentMin && val !== this.currentMax) {
                this.currentMax = val;
                this.setMaxValue(val);
            }
        },
        stopDrag() {
            this.dragging = null;
            document.removeEventListener('mousemove', this.onDrag);
            document.removeEventListener('mouseup', this.stopDrag);
            document.removeEventListener('touchmove', this.onDrag, {
                passive: false,
            });
            document.removeEventListener('touchend', this.stopDrag);
            document.removeEventListener('touchcancel', this.stopDrag);
        },
        onTrackClick(event) {
            if (event.type === 'touchstart') {
                if (event.cancelable) event.preventDefault();
                this._lastTouchStart = Date.now();
            } else if (event.type === 'mousedown' && this._lastTouchStart && Date.now() - this._lastTouchStart < 700) {
                return;
            }

            const val = this.valueFromEvent(event);
            const distToMin = Math.abs(val - this.currentMin);
            const distToMax = Math.abs(val - this.currentMax);
            if (distToMin <= distToMax) {
                this.currentMin = Math.min(val, this.currentMax);
                this.setMinValue(this.currentMin);
            } else {
                this.currentMax = Math.max(val, this.currentMin);
                this.setMaxValue(this.currentMax);
            }
        },
        setInputValue(event, type) {
            const val = Number(event.target.value.replace(/\D/g, ''));
            if (type === 'min') {
                if (val >= this.absoluteMin && val <= this.currentMax) {
                    this.currentMin = val;
                    this.setMinValue(val);
                } else {
                    this.$refs.minInput.value = this.formattedValue(this.currentMin);
                }
            } else {
                if (val <= this.absoluteMax && val >= this.currentMin) {
                    this.currentMax = val;
                    this.setMaxValue(val);
                } else {
                    this.$refs.maxInput.value = this.formattedValue(this.currentMax);
                }
            }
        },
    },

    beforeDestroy() {
        this.stopDrag();
    },
};
